import { constants } from '../constants';

interface FocusTrapOptions {
  autoFocus?: boolean;
  restoreFocus?: boolean;
}

interface InertableElement extends HTMLElement {
  inert: boolean;
}

export const createFocusTrap = (options: FocusTrapOptions = { autoFocus: true, restoreFocus: true }) => {
  const { FOCUS_TRAP_ELEMENTS, KEY_DOWN } = constants;

  let container: HTMLElement | null = null;
  let previousFocus: HTMLElement | null = null;
  let siblingElements: InertableElement[] = [];
  let observer: MutationObserver | null = null;

  // FIXED: Simple dirty flag cache to prevent recalculation on every keydown
  let cachedFocusable: HTMLElement[] = [];
  let needsRefresh = true;

  const getFocusable = (): HTMLElement[] => {
    // FIXED: Use cache to avoid recalculation, only refresh when needed
    if (!needsRefresh && cachedFocusable.length > 0) return cachedFocusable;
    if (!container) return [];

    // FIXED: Replaced getComputedStyle() with offsetWidth/Height + visibility check
    cachedFocusable = Array.from(container.querySelectorAll<HTMLElement>(FOCUS_TRAP_ELEMENTS)).filter(el => {
      // Check dimensions AND element is not hidden via visibility/display
      if (el.offsetWidth === 0 && el.offsetHeight === 0) {
        // Only use getComputedStyle for zero-dimension elements
        const style = getComputedStyle(el);
        return style.display !== 'none' && style.visibility !== 'hidden';
      }
      return true;
    });

    needsRefresh = false;
    return cachedFocusable;
  };

  const handleTabKey = (event: KeyboardEvent, focusableElements: HTMLElement[]): void => {
    if (!container) return;

    const activeElement = document.activeElement as HTMLElement;

    if (!container.contains(activeElement)) {
      event.preventDefault();
      // FIXED: Guard against empty array before accessing [0]
      if (focusableElements.length > 0) focusableElements[0].focus();
      return;
    }

    const currentIndex = focusableElements.indexOf(activeElement);

    // FIXED: Handle -1 case when activeElement not found in focusableElements
    if (currentIndex === -1) {
      event.preventDefault();
      if (focusableElements.length > 0) focusableElements[0].focus();
      return;
    }

    const isFirst = currentIndex === 0;
    const isLast = currentIndex === focusableElements.length - 1;

    if ((event.shiftKey && isFirst) || (!event.shiftKey && isLast)) {
      event.preventDefault();
      focusableElements[event.shiftKey ? focusableElements.length - 1 : 0].focus();
    }
  };

  const handleDocumentKeyDown = (event: KeyboardEvent): void => {
    if (!container) return;

    const focusableElements = getFocusable();
    // FIXED: Guard against empty focusableElements array
    if (!focusableElements.length) return;

    if (event.key === 'Tab') {
      handleTabKey(event, focusableElements);
    }
  };

  const activate = (targetContainer: HTMLElement): void => {
    deactivate();

    container = targetContainer;
    previousFocus = document.activeElement as HTMLElement;
    // FIXED: Mark cache as needing refresh when container changes
    needsRefresh = true;

    // FIXED: More robust sibling element detection with debugging
    try {
      console.log('Body children count:', document.body.children.length);
      console.log('Container:', container);
      console.log('Container tagName:', container?.tagName);

      siblingElements = Array.from(document.body.children).filter((el): el is InertableElement => {
        const isHTMLElement = el instanceof HTMLElement;
        const containsContainer = container ? el.contains(container) : false;

        console.log('Checking element:', el.tagName, 'isHTML:', isHTMLElement, 'contains:', containsContainer);

        return !containsContainer && isHTMLElement;
      });

      console.log('Found sibling elements:', siblingElements.length, siblingElements);
    } catch (error) {
      console.warn('Sibling element detection failed:', error);
      siblingElements = [];
    }

    // FIXED: Add MutationObserver for DOM changes
    if (container) {
      observer = new MutationObserver(() => {
        needsRefresh = true;
      });

      observer.observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['disabled', 'tabindex', 'hidden', 'aria-hidden'],
      });
    }

    siblingElements.forEach(el => {
      if ('inert' in el) {
        el.inert = true;
      }
    });

    document.addEventListener(KEY_DOWN, handleDocumentKeyDown);

    if (options.autoFocus !== false) {
      // FIXED: Wait for DOM to be ready before focusing
      requestAnimationFrame(() => {
        const focusable = getFocusable();
        if (focusable.length > 0) {
          // FIXED: Try to focus with fallback if focus fails
          try {
            focusable[0].focus();
            if (document.activeElement !== focusable[0]) {
              throw new Error('Focus failed');
            }
          } catch {
            // Fallback: focus container when focus fails
            if (container) {
              container.tabIndex = 0;
              container.focus();
            }
          }
        } else {
          // FIXED: Fallback focus container when no focusable children
          if (container) {
            container.tabIndex = 0;
            container.focus();
          }
        }
      });
    }
  };

  const deactivate = (): void => {
    // FIXED: Disconnect observer
    if (observer) {
      observer.disconnect();
      observer = null;
    }

    if (siblingElements.length > 0) {
      siblingElements.forEach(el => {
        if ('inert' in el) {
          el.inert = false;
        }
      });
      siblingElements = [];
    }

    document.removeEventListener(KEY_DOWN, handleDocumentKeyDown);

    if (options.restoreFocus !== false && previousFocus) {
      try {
        previousFocus.focus();
      } catch (error) {
        console.warn('Failed to restore focus:', error);
      }
    }

    container = null;
    previousFocus = null;
    needsRefresh = true;
  };

  return { activate, deactivate };
};
